import { useAiBeautyShotFlow } from '@/composables/ai-upload/useAiBeautyShotFlow';
import { useAiCreditsRemaining } from '@/composables/ai-upload/useAiCreditsRemaining';
import { storedPhotoDataUrl, useAiPhotoTransfer } from '@/composables/ai-upload/useAiPhotoTransfer';
import { useAppearanceComposable } from '@/composables/appearance/AppearanceComposable';
import { useTransferPreview } from '@/composables/useTransferPreview';
import AiPhotoSelectionBlock from '@/pages/ai-product-page/partials/ai-product-info/partials/AiPhotoSelectionBlock.vue';
import AiRegenerateBlock from '@/pages/ai-product-page/partials/ai-product-info/partials/AiRegenerateBlock.vue';
import TextPersonalizationBlock from '@/pages/ai-product-page/partials/ai-product-info/partials/TextPersonalizationBlock.vue';
import { useAiGenerationHistoryStore } from '@/stores/ai-upload/AiGenerationHistoryStore';
import { isTerminalBeautyShotStep, useAiImageGenerationSessionStore } from '@/stores/ai-upload/AiImageGenerationSessionStore';
import { useProductPageStore } from '@/stores/pages/ProductPageStore';
import type { AppPageProps } from '@/types';
import type { AiStyleAdditionObject, CollectionSliderItemObject } from '@/types/generated';
import { usePage } from '@inertiajs/vue3';
import { type Component, type ComputedRef, type Ref, computed, onMounted, ref, watch } from 'vue';

export type AiPanelBlockConfiguration = {
    key: string;
    component: Component;
    props: Record<string, unknown>;
    visible: boolean;
};

type AiPanelPageProps = AppPageProps & {
    related_products?: CollectionSliderItemObject[];
    ai_style_additions?: AiStyleAdditionObject[];
    style_additions_enabled?: boolean;
    ai_credits_max?: number;
    ai_credits_remaining?: {
        remaining: number;
        max: number;
        isDailyThresholdExceeded: boolean;
        isUserDailyLimitExceeded: boolean;
        hoursUntilNextCredit: number | null;
    } | null;
};

export type AiPanelStateReturn = {
    isMounted: Ref<boolean>;
    allBeautyShotsSettled: ComputedRef<boolean>;
    generationCompleted: ComputedRef<boolean>;
    generationFailed: ComputedRef<boolean>;
    generationStarted: ComputedRef<boolean>;
    isRegenerateBlockDisabled: ComputedRef<boolean>;
    hasEverCompleted: Ref<boolean>;
    showUserDailyLimitMessage: ComputedRef<boolean>;
    showThresholdExceededMessage: ComputedRef<boolean>;
    pendingBeautyShotCount: ComputedRef<number>;
    showBeautyShotsBadge: ComputedRef<boolean>;
    showPersonalizationBlock: ComputedRef<boolean>;
    showPhotoSelectionBlock: ComputedRef<boolean>;
    showPhotoBlock: ComputedRef<boolean>;
    styleAdditions: ComputedRef<AiStyleAdditionObject[]>;
    styleAdditionsEnabled: ComputedRef<boolean>;
    panelBlockConfigurations: ComputedRef<AiPanelBlockConfiguration[]>;
    hasPanelContent: ComputedRef<boolean>;
};

export function useAiPanelState(): AiPanelStateReturn {
    const { creditsRemaining, maxCredits, isDailyThresholdExceeded, isUserDailyLimitExceeded, hoursUntilNextCredit } = useAiCreditsRemaining();

    const session = useAiImageGenerationSessionStore();
    const historyStore = useAiGenerationHistoryStore();
    const { transferPreview } = useTransferPreview();
    const photoTransfer = useAiPhotoTransfer();
    const page = usePage<AiPanelPageProps>();
    const productPageStore = useProductPageStore();
    const { isSingleShotFlow } = useAiBeautyShotFlow(productPageStore.configurableProduct);
    const { isTabletUp } = useAppearanceComposable();

    // Seeds the credit limits when AiUploadButton is not mounted. Must stay below `page`: immediate:true runs the getter synchronously.
    watch(
        () => page.props.ai_credits_remaining,
        prop => {
            if (!prop || maxCredits.value !== undefined) {
                return;
            }

            isDailyThresholdExceeded.value = prop.isDailyThresholdExceeded;
            isUserDailyLimitExceeded.value = prop.isUserDailyLimitExceeded;
            hoursUntilNextCredit.value = prop.hoursUntilNextCredit ?? undefined;
            maxCredits.value = prop.max;
            creditsRemaining.value = prop.remaining;
        },
        { immediate: true },
    );

    const isMounted = ref(false);
    onMounted(() => {
        isMounted.value = true;

        // In onMounted: reading storage during setup would run on the server.
        photoTransfer.syncStoredPhoto();
    });

    const allBeautyShotsSettled = computed<boolean>(
        () => session.beautyShots.length > 0 && session.beautyShots.every(shot => isTerminalBeautyShotStep(shot.step)),
    );

    const generationCompleted = computed<boolean>(() => session.isActive && session.step === 'completed' && allBeautyShotsSettled.value);

    const generationFailed = computed<boolean>(() => session.isActive && session.step === 'failed');

    const generationStarted = computed<boolean>(() => isMounted.value && (session.isActive || historyStore.hasHistory));

    // A failed generation is terminal, so retrying stays enabled.
    const isRegenerateBlockDisabled = computed<boolean>(() => {
        if (generationFailed.value) {
            return false;
        }

        return session.isActive && !generationCompleted.value;
    });

    // Latched: historyStore.hasHistory is still false when the second generation starts.
    const hasEverCompleted = ref(false);

    watch(
        generationCompleted,
        (completed: boolean) => {
            if (completed) {
                hasEverCompleted.value = true;
            }
        },
        { immediate: true },
    );

    const isAnyLimitExceeded = computed<boolean>(() => isUserDailyLimitExceeded.value || isDailyThresholdExceeded.value);

    const showUserDailyLimitMessage = computed<boolean>(() => isMounted.value && isUserDailyLimitExceeded.value);
    const showThresholdExceededMessage = computed<boolean>(() => isMounted.value && generationCompleted.value && isDailyThresholdExceeded.value);

    const pendingBeautyShotCount = computed<number>(() => session.beautyShots.filter(shot => !isTerminalBeautyShotStep(shot.step)).length);

    // Badge shows while the remaining shots are still settling after the reveal gate opens.
    const showBeautyShotsBadge = computed<boolean>(() => {
        if (isSingleShotFlow.value) {
            return false;
        }

        // Phones do without it: the thumbnails already show their own spinners.
        if (!isTabletUp.value) {
            return false;
        }

        if (!isMounted.value || !session.isActive || session.step !== 'completed') {
            return false;
        }

        if (!session.isRevealGateOpen) {
            return false;
        }

        return pendingBeautyShotCount.value > 0;
    });

    const showPersonalizationBlock = computed<boolean>(() => {
        if (!isMounted.value) {
            return false;
        }

        return session.isRevealGateOpen || historyStore.hasHistory || hasEverCompleted.value;
    });

    // === true: a product cached before the flag existed must read as without-text.
    const showTextPersonalizationBlock = computed<boolean>(
        () => showPersonalizationBlock.value && productPageStore.configurableProduct.with_text === true,
    );

    // Also tells the page where the order button lives, so it must stay false once a result is in.
    const showPhotoSelectionBlock = computed<boolean>(
        () =>
            isMounted.value &&
            transferPreview.value !== undefined &&
            !session.isRevealGateOpen &&
            !isDailyThresholdExceeded.value &&
            !isUserDailyLimitExceeded.value &&
            !historyStore.hasHistory,
    );

    const showPhotoBlock = computed<boolean>(
        () =>
            isMounted.value &&
            (transferPreview.value !== undefined || storedPhotoDataUrl.value !== undefined) &&
            !isDailyThresholdExceeded.value &&
            !isUserDailyLimitExceeded.value,
    );

    const styleAdditions = computed<AiStyleAdditionObject[]>(() => page.props.ai_style_additions ?? []);
    const styleAdditionsEnabled = computed<boolean>(() => page.props.style_additions_enabled === true);
    const panelBlockConfigurations = computed<AiPanelBlockConfiguration[]>(() => [
        {
            key: 'photo-selection',
            component: AiPhotoSelectionBlock,
            props: { disabled: isRegenerateBlockDisabled.value, isPrimedTransfer: showPhotoSelectionBlock.value },
            visible: showPhotoBlock.value,
        },
        {
            key: 'personalization',
            component: TextPersonalizationBlock,
            props: { disabled: isRegenerateBlockDisabled.value || isAnyLimitExceeded.value },
            visible: showTextPersonalizationBlock.value,
        },
        {
            key: 'style-additions',
            component: AiRegenerateBlock,
            props: {
                disabled: isRegenerateBlockDisabled.value || isAnyLimitExceeded.value,
                additions: styleAdditions.value,
            },
            visible: showPersonalizationBlock.value && styleAdditionsEnabled.value && styleAdditions.value.length > 0,
        },
    ]);

    // Keeps AiPanelBody from mounting as an empty bordered sliver when every block is hidden.
    const hasPanelContent = computed<boolean>(
        () =>
            showBeautyShotsBadge.value ||
            panelBlockConfigurations.value.some((config: AiPanelBlockConfiguration) => config.visible) ||
            showUserDailyLimitMessage.value ||
            showThresholdExceededMessage.value,
    );

    return {
        isMounted,
        allBeautyShotsSettled,
        generationCompleted,
        generationFailed,
        generationStarted,
        isRegenerateBlockDisabled,
        hasEverCompleted,
        showUserDailyLimitMessage,
        showThresholdExceededMessage,
        pendingBeautyShotCount,
        showBeautyShotsBadge,
        showPersonalizationBlock,
        showPhotoSelectionBlock,
        showPhotoBlock,
        styleAdditions,
        styleAdditionsEnabled,
        panelBlockConfigurations,
        hasPanelContent,
    };
}
